import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  resolveEventWallTimezone,
  scheduleInputToEventWallMinute,
} from "./eventWallMinuteStrings";
import { inclusiveEndCalendarDayFromPhaseEndWall } from "./metaBudgetScheduleWallSnap";

dayjs.extend(utc);
dayjs.extend(timezone);

function normalizedWallsForLabels(
  start: string,
  end: string,
  eventTimezone: string
): { tz: string; wallStart: string; wallEnd: string } | null {
  if (!start || !end) return null;
  const tz = resolveEventWallTimezone(eventTimezone);
  const wallStart = scheduleInputToEventWallMinute(start, eventTimezone);
  const wallEnd = scheduleInputToEventWallMinute(end, eventTimezone);
  if (!wallStart || !wallEnd) return null;
  return { tz, wallStart, wallEnd };
}

/** Review row: format campaign schedule wall range with timezone label. */
export function formatCampaignScheduleWallRange(
  start: string,
  end: string,
  eventTimezone: string
): string {
  const walls = normalizedWallsForLabels(start, end, eventTimezone);
  if (!walls) return "—";
  const { tz, wallStart, wallEnd } = walls;
  const fmt = (wall: string) => {
    const d = dayjs.tz(wall.slice(0, 16), tz);
    if (!d.isValid()) return wall;
    return d.format(d.minute() === 0 ? "MMM D 'YY ha" : "MMM D 'YY h:mma");
  };
  const tzShort = tz.replace(/_/g, " ");
  return `${fmt(wallStart)} – ${fmt(wallEnd)} (${tzShort})`;
}

const SCHEDULED_PHASE_RANGE_DATE_FMT = "MMM D, YYYY";

export const SCHEDULED_PHASE_CAMPAIGN_START_LABEL = "Campaign start";
export const SCHEDULED_PHASE_CAMPAIGN_END_LABEL = "Campaign end";

export type FormatScheduledPhaseWallRangeOptions = {
  /** Zero-based index when rendering a phased budget list (review, detail). */
  phaseIndex?: number;
  /** Total phase count; with {@link FormatScheduledPhaseWallRangeOptions.phaseIndex}, first start / last end use campaign boundary labels. */
  totalPhases?: number;
};

/**
 * Scheduled budget phase line on campaign review: **calendar dates** in the event zone (no
 * times-of-day). The end wall is typically the following local midnight (exclusive); the label
 * uses the **inclusive last calendar day** (same rule as phased budget date pickers).
 */
export function formatScheduledPhaseWallRange(
  start: string,
  end: string,
  eventTimezone: string,
  options?: FormatScheduledPhaseWallRangeOptions
): string {
  const walls = normalizedWallsForLabels(start, end, eventTimezone);
  if (!walls) return "—";
  const { tz, wallStart, wallEnd } = walls;
  const dStart = dayjs.tz(wallStart.slice(0, 16), tz);
  if (!dStart.isValid()) return "—";
  const inclusiveEnd = inclusiveEndCalendarDayFromPhaseEndWall(end, eventTimezone);
  const endDateLabel = inclusiveEnd?.isValid()
    ? inclusiveEnd.tz(tz).format(SCHEDULED_PHASE_RANGE_DATE_FMT)
    : dayjs.tz(wallEnd.slice(0, 16), tz).format(SCHEDULED_PHASE_RANGE_DATE_FMT);

  const totalPhases = options?.totalPhases ?? 0;
  const phaseIndex = options?.phaseIndex;
  const useCampaignBoundaryLabels =
    totalPhases > 0 && phaseIndex != null && phaseIndex >= 0 && phaseIndex < totalPhases;

  const startLabel =
    useCampaignBoundaryLabels && phaseIndex === 0
      ? SCHEDULED_PHASE_CAMPAIGN_START_LABEL
      : dStart.format(SCHEDULED_PHASE_RANGE_DATE_FMT);
  const endLabel =
    useCampaignBoundaryLabels && phaseIndex === totalPhases - 1
      ? SCHEDULED_PHASE_CAMPAIGN_END_LABEL
      : endDateLabel;

  return `${startLabel} → ${endLabel}`;
}
